import { Button } from "@shared/ui/button";
import { Upload, Download, FileText, CheckCircle2, X } from "lucide-react";
import React, { useState, useRef } from "react";
import { Progress } from "@shared/ui/progress";
import {
  useGetConsumerCsvTemplate,
  useValidateConsumerCsv,
} from "@features/cx/disconnect/hooks";

interface CsvUploadTabProps {
  onAddConsumers: (consumers: any[]) => void;
  uploadedFile?: string | null;
  onUploadedFileChange?: (fileName: string | null) => void;
}

const CsvUploadTab: React.FC<CsvUploadTabProps> = ({ 
  onAddConsumers,
  uploadedFile,
  onUploadedFileChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [internalUploadedFile, setInternalUploadedFile] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  
  const currentUploadedFile = uploadedFile !== undefined ? uploadedFile : internalUploadedFile;
  
  const handleSetUploadedFile = (name: string | null) => {
    setInternalUploadedFile(name);
    onUploadedFileChange?.(name);
  };
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { downloadTemplate, isDownloading: isDownloadingTemplate } =
    useGetConsumerCsvTemplate();
  const { mutate: validateCsv, isPending: isValidating } =
    useValidateConsumerCsv();

  const handleUpload = (file: File) => {
    setFileName(file.name);
    setIsUploading(true);
    setIsComplete(false);
    setProgress(0);
    handleSetUploadedFile(null);

    // Simulate UI progress while waiting for API
    const interval = setInterval(() => {
      setProgress((prev) => (prev < 90 ? prev + 5 : prev));
    }, 300);

    validateCsv(file, {
      onSuccess: (data) => {
        clearInterval(interval);
        setProgress(100);
        setIsComplete(true);

        // Safely extract 'found' array from the API response
        const rawFound = data?.result?.found || data?.found || [];
        
        const consumers = rawFound.map((item: any) => ({
          id: item.id,
          consumerNo: item.consumerNo ,
          firstName: item.firstName ,
          lastName: item.lastName ,
          contactNumber: item.contactNumber,
          email: item.email,
          status: item.status,
          statusMessage: item.statusMessage,
          outstandingBalance:item.outstandingBalance,
          premise:item.premiseDisplay,
          
        }));

        setTimeout(() => {
          onAddConsumers(consumers);
          setIsUploading(false);
          setIsComplete(false);
          handleSetUploadedFile(file.name);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }, 800);
      },
      onError: () => {
        clearInterval(interval);
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
    });

    uploadIntervalRef.current = interval;
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleCancelUpload = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
    }
    setIsUploading(false);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = () => {
    handleSetUploadedFile(null);
    setFileName("");
    setProgress(0);
  };

  const handleDownloadTemplate = () => {
    downloadTemplate();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith(".csv")) {
      handleUpload(file);
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        className="hidden"
      />

      {!isUploading && !currentUploadedFile ? (
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="border-2 border-dashed border-blue-200 rounded-xl bg-blue-50/20 p-10 flex flex-col items-center justify-center text-center transition-all hover:bg-blue-50/40 hover:border-blue-300 min-h-[300px] group"
        >
          <div className="bg-blue-50 p-4 rounded-full mb-4 group-hover:scale-110 transition-transform">
            <Upload className="h-10 w-10 text-blue-400" />
          </div>
          <p className="text-neutral-600 mb-6 max-w-xs">
            Drag & drop a CSV file here or click to browse
            <span className="block text-xs text-neutral-400 mt-1 italic">
              Maximum file size: 5MB
            </span>
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant="ghost"
              onClick={handleDownloadTemplate}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 gap-2 font-medium"
            >
              <Download className="h-4 w-4" />
              Download Template
            </Button>
            <Button
              variant="outline"
              className="bg-white border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 font-medium px-6 shadow-sm"
              onClick={triggerFileSelect}
            >
              Choose CSV File
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-solid border-blue-100 rounded-xl bg-white p-10 flex flex-col items-center justify-center text-center transition-all min-h-[300px]">
          {isUploading ? (
            <div className="w-full max-w-md space-y-6 flex flex-col items-center animate-in fade-in zoom-in duration-300">
              <div className="bg-neutral-50 p-6 items-center rounded-xl border border-neutral-100 shadow-sm flex flex-col gap-4 w-full relative group">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
                <div className="text-center w-full px-4">
                  <p className="text-sm font-semibold text-neutral-800 break-all">
                    {fileName}
                  </p>
                  <p className="text-xs text-neutral-400">
                    12.4 KB • {isComplete ? "Upload complete" : "Uploading..."}
                  </p>
                </div>
                {!isComplete && (
                  <button
                    onClick={handleCancelUpload}
                    className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-neutral-100 text-neutral-400 transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
                {isComplete && (
                  <div className="text-green-500 animate-in zoom-in fade-in duration-300">
                    <CheckCircle2 size={32} />
                  </div>
                )}
              </div>

              <div className="w-full space-y-2">
                <div className="flex justify-between items-center px-1">
                  <span className="text-sm font-medium text-neutral-600">
                    {progress}%
                  </span>
                  <span className="text-xs text-neutral-400">
                    {isComplete ? "Done" : `${progress}% of 100%`}
                  </span>
                </div>
                <Progress
                  value={progress}
                  className="h-2 bg-neutral-100"
                  indicatorClassName={
                    isComplete ? "bg-green-500" : "bg-blue-500"
                  }
                />
              </div>

              <p className="text-xs text-neutral-500 animate-pulse font-medium">
                {isComplete
                  ? "Successfully processed records!"
                  : "Analyzing CSV data and validating consumers..."}
              </p>
            </div>
          ) : (
            <div className="w-full max-w-md space-y-6 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-green-50 p-6 rounded-full mb-2">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>

              <div className="space-y-1">
                <h4 className="text-xl font-bold text-neutral-900">
                  File Uploaded Successfully
                </h4>
                <p className="text-sm text-neutral-500">
                  The consumer data has been processed and added to your review
                  list.
                </p>
              </div>

              <div className="bg-neutral-50 p-6 items-center rounded-xl border border-neutral-100 shadow-sm flex flex-col gap-4 w-full">
                <div className="bg-green-100 p-3 rounded-lg">
                  <FileText className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-center w-full px-4">
                  <p className="text-sm font-semibold text-neutral-800 break-all">
                    {currentUploadedFile}
                  </p>
                  <p className="text-xs text-neutral-400 uppercase tracking-wider font-bold">
                    Processed • 12.4 KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-2 font-medium"
                >
                  <X size={16} />
                  <span>Remove File</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-100 transition-all hover:bg-neutral-100/50">
        <div className="bg-neutral-200 p-1 rounded mt-0.5">
          <Upload className="h-3 w-3 text-neutral-500" />
        </div>
        <p className="text-xs text-neutral-500 leading-relaxed font-medium">
          CSV must contain a column with consumer/account IDs. Max 500 rows.
        </p>
      </div>
    </div>
  );
};

export default CsvUploadTab;
